export const billetPdfUrl = (chargeId: string): string =>
  `${process.env.REACT_APP_API_URL}/public/charges/${chargeId}/billet-pdf`;

/** O mínimo de uma aba que nos interessa — o `Window` real tem muito mais. */
type OpenedTab = { location: { href: string }; closed: boolean; close: () => void };

type OpenOptions = {
  chargeId: string;
  /** PDF oficial do banco: a saída de emergência. */
  bankPdfUrl?: string;
  fetchImpl?: typeof fetch;
  open?: (url: string, target: string) => OpenedTab | null;
  createObjectURL?: (blob: Blob) => string;
  navigate?: (url: string) => void;
};

/**
 * Abre o boleto desenhado por nós; se a rota recusar, leva a mesma aba para o
 * PDF oficial do banco.
 *
 * **A aba é aberta ANTES do fetch, de propósito.** Safari e iOS só permitem
 * abrir aba dentro do gesto que o usuário fez; um `window.open` depois de um
 * `await` é bloqueado em silêncio. Isto é um checkout — tocar no botão e não
 * acontecer nada é o pior desfecho possível, pior que abrir a aba errada.
 *
 * A ficha só é desenhada quando a linha digitável confere com o código de
 * barras, e quando não confere quem paga não tem nada a fazer com essa
 * informação: para ele o que importa é ter um boleto que o banco aceite. Por
 * isso o fallback é silencioso, e o erro só aparece quando não existe nenhum
 * dos dois.
 */
export async function openBilletPdf({
  chargeId,
  bankPdfUrl,
  fetchImpl = fetch,
  open = (url, target) => window.open(url, target) as OpenedTab | null,
  createObjectURL = (blob) => window.URL.createObjectURL(blob),
  navigate = (url) => window.location.assign(url),
}: OpenOptions): Promise<void> {
  const tab = open('', '_blank');

  /** Leva o destino para a aba aberta; sem aba (bloqueador), usa a janela atual. */
  const go = (url: string) => {
    if (tab) {
      // O usuário pode ter fechado a aba enquanto o fetch corria.
      if (!tab.closed) tab.location.href = url;
      return;
    }
    navigate(url);
  };

  try {
    const response = await fetchImpl(billetPdfUrl(chargeId));
    if (response.ok) {
      go(createObjectURL(await response.blob()));
      return;
    }
  } catch {
    // Rede fora ou resposta ilegível: o fallback abaixo é a única chance de pagar.
  }

  if (bankPdfUrl) {
    go(bankPdfUrl);
    return;
  }

  // Aba em branco pendurada é pior que aba nenhuma: quem paga troca de aba, vê
  // uma página vazia e acha que o boleto é isso.
  if (tab && !tab.closed) tab.close();

  throw new Error('Não foi possível abrir o boleto. Entre em contato com o vendedor.');
}
